import { dashboardErrorClass, notConnectedToolkit, outageServiceName, type DashboardErrorClass } from './dashboard-error-kind';

/**
 * A failed widget tool call, as `/api/tool-call` answers it and the widget host reads it.
 *
 * The harness marks every answer a harness wrote itself (`x-symphony-tool-call-answer: harness`, see
 * `OWN_ANSWER_HEADER` in orion-harness-poc `endpoints/tool-call-java-bridge.ts`): its own handler's, and the Java
 * door's JSON answers that the door stamped as its own. That failure text is written for the agent that built the
 * widget — a refusal of the call, or the tool's own error — and the "Ask Symphony to fix" hand-off and the widget's
 * runtime-error report need it, so a marked answer's text is passed on, capped. An unmarked answer may be a
 * gateway's page (the bridge wraps a non-JSON body as `{ error: <body> }`), so it is reduced to the verdict the
 * dashboard banner (`dashboard-error-kind.ts`) would have reached, and the app or service it names.
 *
 * Pure + import-free of the app graph, so jest exercises both halves directly.
 */
export interface ToolCallFailure {
  error: string;
  /** The harness's own failure text, from a marked answer only. */
  detail?: string;
  /** The toolkit with no usable connection (`gmail`). */
  connector?: string;
  /** The external endpoint that did not answer, reduced to its host. */
  service?: string;
}

export const OWN_ANSWER_HEADER = 'x-symphony-tool-call-answer';
export const OWN_ANSWER = 'harness';
const MAX_DETAIL = 500;
const SLUG = /^[a-z0-9][a-z0-9_.-]{0,63}$/i;
const HOST = /^[a-z0-9][a-z0-9 ._-]{0,99}$/i;
const HARNESS_REFUSALS = new Set([400, 403, 404]);

function parse(text: string): Record<string, unknown> | null {
  try {
    const value = JSON.parse(text) as unknown;
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function shaped(value: unknown, pattern: RegExp): string | undefined {
  return typeof value === 'string' && pattern.test(value) ? value : undefined;
}

function verdict(line: string): { failure: ToolCallFailure; kind: DashboardErrorClass } {
  const kind = dashboardErrorClass(line);
  switch (kind) {
    case 'notConnected': {
      const connector = shaped(notConnectedToolkit(line), SLUG);
      return { kind, failure: { error: 'CONNECTOR_DISCONNECTED', ...(connector ? { connector } : {}) } };
    }
    case 'rateLimited':
      return { kind, failure: { error: 'rate_limited' } };
    case 'outage': {
      const service = shaped(outageServiceName(line), HOST);
      return { kind, failure: { error: 'service_unavailable', ...(service ? { service } : {}) } };
    }
    default:
      return { kind, failure: { error: 'tool_failed' } };
  }
}

/**
 * Server half: the harness's non-ok answer, reduced to what a widget host may be told. `kind` is the banner's
 * class of the failure, for the log level.
 */
export function toolCallFailure(status: number, text: string, ownAnswer: boolean): { failure: ToolCallFailure; kind: DashboardErrorClass } {
  const body = parse(text);
  if (body?.code === 'CONNECTOR_DISCONNECTED') {
    const connector = shaped(body.connector, SLUG);
    return { kind: 'notConnected', failure: { error: 'CONNECTOR_DISCONNECTED', ...(connector ? { connector } : {}) } };
  }
  if (body?.code === 'PARTNER_PLAN_INACTIVE') {
    const sentence = typeof body.message === 'string' && body.message ? body.message : typeof body.error === 'string' ? body.error : '';
    return {
      kind: 'defect',
      failure: { error: 'PARTNER_PLAN_INACTIVE', ...(ownAnswer && sentence ? { detail: sentence.slice(0, MAX_DETAIL) } : {}) },
    };
  }
  const harnessError = typeof body?.error === 'string' ? body.error : '';
  // Every status gets the banner's verdict: a 429 refused for volume or a 503 that never reached the service is
  // not ours to fix, whatever its status.
  const classified = verdict(harnessError || text);
  const ours = status === 422 ? 'tool_failed' : 'upstream_error';
  if (ownAnswer && harnessError) {
    const error = HARNESS_REFUSALS.has(status) ? 'tool_refused' : classified.kind === 'defect' ? ours : classified.failure.error;
    return { kind: classified.kind, failure: { error, detail: harnessError.slice(0, MAX_DETAIL) } };
  }
  return classified.kind === 'defect' ? { kind: 'defect', failure: { error: ours } } : classified;
}

/**
 * Client half: the line the widget's rejected call carries. A marked answer's text is the line, as it always
 * was; otherwise the verdict is phrased so `dashboard-error-kind.ts` places it in the same class the service's
 * own words would have.
 */
export function toolCallFailureLine(tool: string, failure: Partial<ToolCallFailure>, status: number): string {
  if (failure.detail) return failure.detail;
  switch (failure.error) {
    case 'CONNECTOR_DISCONNECTED':
      return failure.connector ? `CONNECTOR_DISCONNECTED for toolkit ${failure.connector}` : 'CONNECTOR_DISCONNECTED';
    case 'rate_limited':
      return `${tool}: rate limited`;
    case 'service_unavailable':
      return failure.service
        ? `[UPSTREAM UNAVAILABLE] calling ${tool} on "${failure.service}": service unavailable`
        : `[UPSTREAM UNAVAILABLE] ${tool}: service unavailable`;
    case 'tool_refused':
      return `${tool}: refused (HTTP ${status})`;
    case 'tool_failed':
      return `${tool} failed (HTTP ${status})`;
    default:
      return failure.error || `HTTP ${status}`;
  }
}
